import { Link } from "react-router-dom";
import type { Article } from "@/lib/api";
import { formatArticlePrice, formatDate } from "@/lib/format";
import { CATEGORIES } from "@/lib/categories";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { ShareButtons } from "./ShareButtons";

const categoryNameMap = Object.fromEntries(
  CATEGORIES.map((c) => [c.slug, c.name]),
);

export function ArticleHeader({ article }: { article: Article }) {
  return (
    <div className="mb-8">
      {article.coverImageUrl && (
        <img
          src={article.coverImageUrl}
          alt={article.title}
          className="w-full max-h-80 object-cover rounded-xl mb-6"
        />
      )}
      <h1 className="text-3xl md:text-4xl font-display font-bold mb-2">
        {article.title}
      </h1>
      {article.subtitle && (
        <p className="text-lg text-grey font-body mb-4">{article.subtitle}</p>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        {article.writer && (
          <Link
            to={`/writer/${article.writerId}`}
            className="flex items-center gap-2 hover:opacity-80"
          >
            <Avatar
              src={article.writer.avatarUrl}
              name={article.writer.displayName}
            />
            <span className="font-body font-bold text-sm">
              {article.writer.displayName}
            </span>
          </Link>
        )}
        {article.publishedAt && (
          <span className="text-sm text-beige font-body">
            {formatDate(article.publishedAt)}
          </span>
        )}
        <Badge variant="orange">
          {formatArticlePrice(article.priceUsdc, article.priceEurc)}
        </Badge>
        {article.category && categoryNameMap[article.category] && (
          <span className="text-xs font-body text-beige bg-beige/10 px-2 py-0.5 rounded">
            {categoryNameMap[article.category]}
          </span>
        )}
      </div>
      {(article.topics || []).length > 0 && (
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {article.topics.map((topic) => (
            <Link
              key={topic}
              to={`/?topic=${encodeURIComponent(topic)}`}
              className="text-xs font-body text-grey border border-beige/30 px-2 py-0.5 rounded-full hover:border-orange/50 hover:text-orange transition-colors"
            >
              {topic}
            </Link>
          ))}
        </div>
      )}
      <div className="mt-3">
        <ShareButtons articleId={article.id} title={article.title} />
      </div>
    </div>
  );
}
